// /admin/audit/exports — list audit-log CSV exports.
//
// Replicates the v5 mockup at access-preview-v5.html lines 3057–3375.
// Each row shows: status pill, range/filter summary, who created it,
// row count + size, expires-in, and a Download button when the row is ready.
//
// Auto-polling: while at least one row is in queued/running state the page
// re-fetches every 2s so the operator can watch the row transition without
// reloading.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, useApiErrorHandler } from "@/api";
import {
  Button,
  StatusChip,
  Table,
  type StatusTone,
  type TableColumn,
} from "@/components";
import {
  auditClient,
  type AuditExport,
  type AuditExportFilterSpec,
  type AuditExportStatus,
} from "@/data/clients/auditClient";
import { useRole } from "@/role";
import "../AdminShared.css";
import "./Audit.css";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusTone(status: AuditExportStatus): StatusTone {
  switch (status) {
    case "ready":
      return "success";
    case "running":
      return "info";
    case "queued":
      return "info";
    case "failed":
      return "danger";
    case "expired":
      return "warning";
    default:
      return "info";
  }
}

function statusLabel(status: AuditExportStatus): string {
  switch (status) {
    case "queued":
      return "Queued";
    case "running":
      return "Running";
    case "ready":
      return "Ready";
    case "failed":
      return "Failed";
    case "expired":
      return "Expired";
    default:
      return status;
  }
}

function formatBytes(n: number): string {
  if (!n || n <= 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toISOString().replace("T", " ").slice(0, 16) + " UTC";
  } catch {
    return iso;
  }
}

function expiresIn(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    const ms = Date.parse(iso) - Date.now();
    if (Number.isNaN(ms)) return iso;
    if (ms <= 0) return "expired";
    const days = Math.floor(ms / 86400000);
    if (days >= 1) return `in ${days}d`;
    const hrs = Math.floor(ms / 3600000);
    if (hrs >= 1) return `in ${hrs}h`;
    const mins = Math.max(1, Math.floor(ms / 60000));
    return `in ${mins}m`;
  } catch {
    return iso;
  }
}

function describeFilterSpec(spec: AuditExportFilterSpec | null): string {
  if (!spec) return "—";
  const bits: string[] = [];
  if (spec.from && spec.to) bits.push(`${spec.from} → ${spec.to}`);
  if (spec.locations && spec.locations.length > 0) {
    bits.push(`${spec.locations.length} location${spec.locations.length === 1 ? "" : "s"}`);
  }
  if (spec.event_types && spec.event_types.length > 0) {
    bits.push(`${spec.event_types.length} action${spec.event_types.length === 1 ? "" : "s"}`);
  }
  if (spec.actor_aliases && spec.actor_aliases.length > 0) {
    bits.push(`${spec.actor_aliases.length} actor${spec.actor_aliases.length === 1 ? "" : "s"}`);
  }
  if (spec.q && spec.q.length > 0) {
    bits.push(`q="${spec.q}"`);
  }
  if (spec.include === "all") bits.push("all events");
  return bits.length === 0 ? "—" : bits.join(" · ");
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AdminAuditExportsPage(): JSX.Element {
  const handleApiError = useApiErrorHandler();
  const { scopedFetchToken } = useRole();

  const [rows, setRows] = useState<AuditExport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const resp = await auditClient.listExports();
      setRows(resp.exports ?? []);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.code ?? String(err.status));
        handleApiError(err, { actionLabel: "Load audit exports" });
      } else {
        setError("unknown");
      }
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [handleApiError]);

  useEffect(() => {
    setLoading(true);
    void reload();
  }, [reload, scopedFetchToken]);

  // Auto-poll while any row is in flight.
  const inFlight = useMemo(
    () => rows.some((r) => r.status === "queued" || r.status === "running"),
    [rows],
  );
  useEffect(() => {
    if (!inFlight) return;
    const t = window.setInterval(() => {
      void reload();
    }, 2000);
    return () => window.clearInterval(t);
  }, [inFlight, reload]);

  const summary = useMemo(() => {
    const ready = rows.filter((r) => r.status === "ready").length;
    const running = rows.filter(
      (r) => r.status === "queued" || r.status === "running",
    ).length;
    const failed = rows.filter((r) => r.status === "failed").length;
    return { ready, running, failed };
  }, [rows]);

  const columns: ReadonlyArray<TableColumn<AuditExport>> = useMemo(
    () => [
      {
        key: "status",
        header: "Status",
        width: "120px",
        render: (row) => (
          <StatusChip tone={statusTone(row.status)}>
            {statusLabel(row.status)}
          </StatusChip>
        ),
      },
      {
        key: "filters",
        header: "Range / filters",
        render: (row) => (
          <span
            className="rb-audit-subs__filters-cell"
            title={describeFilterSpec(row.filterSpec)}
          >
            {describeFilterSpec(row.filterSpec)}
          </span>
        ),
      },
      {
        key: "created",
        header: "Created",
        width: "200px",
        render: (row) => (
          <div>
            <div>
              <code className="mono mono--sm">{formatTimestamp(row.createdAt)}</code>
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted, #6b7280)" }}>
              by {row.createdBy}
            </div>
          </div>
        ),
      },
      {
        key: "size",
        header: "Rows / size",
        width: "150px",
        render: (row) =>
          row.status === "ready" ? (
            <div>
              <div>
                <b>{row.rowCount.toLocaleString()}</b> rows
              </div>
              <div style={{ fontSize: 12, color: "var(--text-muted, #6b7280)" }}>
                {formatBytes(row.sizeBytes)}
              </div>
            </div>
          ) : row.status === "failed" ? (
            <span
              className="admin-muted"
              title={row.errorMessage ?? undefined}
              style={{ fontSize: 12 }}
            >
              {row.errorMessage ? row.errorMessage.slice(0, 32) : "—"}
            </span>
          ) : (
            <span className="admin-muted">—</span>
          ),
      },
      {
        key: "expires",
        header: "Expires",
        width: "120px",
        render: (row) =>
          row.status === "ready" ? (
            <span className="admin-muted">{expiresIn(row.expiresAt)}</span>
          ) : (
            <span className="admin-muted">—</span>
          ),
      },
      {
        key: "actions",
        header: "",
        width: "180px",
        align: "right",
        render: (row) =>
          row.status === "ready" && row.downloadUrl ? (
            <a
              href={row.downloadUrl}
              target="_blank"
              rel="noreferrer"
              data-testid={`download-${row.exportId}`}
            >
              <Button variant="primary" size="sm">
                Download CSV
              </Button>
            </a>
          ) : (
            <span className="admin-muted" style={{ fontSize: 12 }}>
              {row.status === "running" || row.status === "queued"
                ? "Generating…"
                : ""}
            </span>
          ),
      },
    ],
    [],
  );

  return (
    <section className="admin-page">
      <header className="rb-audit-subs__header">
        <Link to="/admin/audit" className="rb-audit-subs__back">
          ← Back to Audit log
        </Link>
        <div className="rb-audit-subs__title-row">
          <h1 className="page-title" style={{ margin: 0 }}>
            Audit exports
          </h1>
        </div>
        <div className="rb-audit-subs__caption">
          {loading
            ? "Loading…"
            : rows.length === 0
              ? "No exports yet."
              : `${summary.ready} ready · ${summary.running} in flight${
                  summary.failed > 0 ? ` · ${summary.failed} failed` : ""
                }`}
        </div>
      </header>

      {error !== null && (
        <div className="card" style={{ marginBottom: 12 }}>
          <StatusChip tone="danger">
            <code className="mono mono--sm">{error}</code>
          </StatusChip>
          <span className="admin-muted"> Failed to load exports.</span>
        </div>
      )}

      {!loading && rows.length === 0 && error === null ? (
        <div className="rb-audit-subs__empty">
          <div className="rb-audit-subs__empty-title">No exports yet</div>
          <p style={{ margin: 0, fontSize: 13 }}>
            Open the{" "}
            <Link to="/admin/audit" style={{ color: "var(--primary-500)" }}>
              audit log
            </Link>
            , adjust filters to taste, then click <b>Export</b> to queue a
            CSV.
          </p>
        </div>
      ) : (
        <Table
          columns={columns}
          rows={rows}
          loading={loading}
          rowKey={(r) => r.exportId}
          emptyState="No exports yet."
        />
      )}
    </section>
  );
}
